import { useMemo, useState } from "react";
import { Download, Leaf, PiggyBank, Coins, Trees } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FACTORS, calcImpact } from "@/lib/data";
import { clp, num } from "@/lib/api";
import { downloadCertificate } from "@/lib/pdf";

const Metric = ({ icon: Icon, label, value, testId, big }) => (
  <div className={`rounded-xl border border-[#1b3d30] bg-[#0d1b1e] p-4 ${big ? "sm:col-span-2" : ""}`}>
    <p className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400"><Icon className="w-4 h-4 text-[#00f2a1]" />{label}</p>
    <p className={`font-display font-bold text-white mt-2 tabular-nums ${big ? "text-4xl text-[#00f2a1]" : "text-2xl"}`} data-testid={testId}>{value}</p>
  </div>
);

export const ImpactCalculator = () => {
  const [material, setMaterial] = useState("aluminio");
  const [tons, setTons] = useState("10");
  const [company, setCompany] = useState("");
  const factor = FACTORS.find((f) => f.id === material);
  const impact = useMemo(() => calcImpact(factor, tons), [factor, tons]);

  return (
    <div className="grid lg:grid-cols-5 gap-6 bg-[#0a1f18] border border-[#1b3d30] rounded-3xl p-6 sm:p-8 text-white" data-testid="esg-calculator">
      <div className="lg:col-span-2 space-y-5">
        <div className="space-y-2">
          <Label className="text-slate-300">Tipo de material</Label>
          <Select value={material} onValueChange={setMaterial}>
            <SelectTrigger className="bg-[#122a21] border-[#1b3d30] text-white h-12" data-testid="esg-calculator-material-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FACTORS.map((f) => <SelectItem key={f.id} value={f.id} data-testid={`esg-material-${f.id}`}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">Peso (toneladas)</Label>
          <Input type="number" min="0" step="0.1" value={tons} onChange={(e) => setTons(e.target.value)}
            className="bg-[#122a21] border-[#1b3d30] text-white h-12 font-mono text-lg" data-testid="esg-calculator-weight-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">Empresa (para el certificado)</Label>
          <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Ej: Metalúrgica Andes SpA"
            className="bg-[#122a21] border-[#1b3d30] text-white h-12" data-testid="esg-calculator-company-input" />
        </div>
        <Button className="w-full h-12 rounded-full font-semibold hover:bg-[#00d890] hover:shadow-[0_0_22px_rgba(0,242,161,0.45)] transition-[background-color,box-shadow] duration-300"
          disabled={!(Number(tons) > 0)} onClick={() => downloadCertificate({ company, factor, tons: Number(tons), impact })} data-testid="esg-calculator-download-btn">
          <Download className="w-4 h-4" /> Descargar certificado (PDF)
        </Button>
        <p className="text-[11px] text-slate-500 leading-relaxed">Factores de emisión y costos referenciales estimados. El PDF es una simulación, no un certificado oficial.</p>
      </div>
      <div className="lg:col-span-3 grid sm:grid-cols-2 gap-4 content-start">
        <Metric big icon={Leaf} label="CO₂ eq. mitigado" value={`${num(impact.co2, 2)} t`} testId="esg-result-co2" />
        <Metric icon={PiggyBank} label="Ahorro en vertedero" value={clp(impact.landfill)} testId="esg-result-landfill" />
        <Metric icon={Coins} label="Valor materia prima secundaria" value={clp(impact.value)} testId="esg-result-value" />
        <Metric big icon={Trees} label="Equivale a la absorción anual de" value={`${num(impact.trees, 0)} árboles`} testId="esg-result-trees" />
      </div>
    </div>
  );
};
